import { configService } from './configService';

export type PaymentProvider = 'paypal' | 'stripe';

export interface PaymentDetails {
  orderId?: string;
  paymentIntentId?: string;
  payerId?: string;
  payerEmail?: string;
  payerName?: string;
  amount: number;
  currency: string;
  status: string;
  timestamp: string;
  paymentType: 'deposit' | 'full';
  isFullPayment: boolean;
  paymentMethod: PaymentProvider;
}

export interface PaymentConfig {
  provider: PaymentProvider;
  amount: number;
  currency: string;
  description: string;
  adults: number;
  children: number;
  paymentType: 'deposit' | 'full';
}

class PaymentService {
  private static instance: PaymentService;

  private constructor() {}

  public static getInstance(): PaymentService {
    if (!PaymentService.instance) {
      PaymentService.instance = new PaymentService();
    }
    return PaymentService.instance;
  }

  /**
   * Calcola l'importo dell'acconto (5€ per persona)
   */
  public calculateDepositAmount(adults: number, children: number, email?: string): number {
    // Se è un'email di test, restituisci sempre 0.99€
    if (this.isProductionTestEmail(email)) {
      console.log('🧪 [TEST] Email di test rilevata, acconto ridotto a €0.99');
      return 0.99;
    }
    
    return (adults + children) * 5;
  }

  /**
   * Calcola l'importo totale (adulti 25€, bambini 15€)
   */
  public calculateFullAmount(adults: number, children: number, email?: string): number {
    // Se è un'email di test, restituisci sempre 0.99€
    if (this.isProductionTestEmail(email)) {
      console.log('🧪 [TEST] Email di test rilevata, importo totale ridotto a €0.99');
      return 0.99;
    }
    
    return (adults * 25) + (children * 15);
  }

  /**
   * Controlla se è un'email di test per produzione
   */
  private isProductionTestEmail(email?: string): boolean {
    const testEmails = ['alberto.malberti@gmail.com'];
    return email ? testEmails.includes(email.toLowerCase()) : false;
  }

  /**
   * Calcola l'importo da pagare in base al tipo di pagamento
   */
  public calculatePaymentAmount(adults: number, children: number, paymentType: 'deposit' | 'full', email?: string): number {
    // Se è un'email di test, restituisci sempre 0.99€
    if (this.isProductionTestEmail(email)) {
      console.log('🧪 [TEST] Email di test rilevata, importo ridotto a €0.99');
      return 0.99;
    }

    if (paymentType === 'full') {
      return this.calculateFullAmount(adults, children, email);
    } else {
      return this.calculateDepositAmount(adults, children, email);
    }
  }

  /**
   * Calcola il saldo rimanente dopo l'acconto
   */
  public calculateRemainingAmount(adults: number, children: number, email?: string): number {
    const fullAmount = this.calculateFullAmount(adults, children, email);
    const depositAmount = this.calculateDepositAmount(adults, children, email);
    return fullAmount - depositAmount;
  }

  /**
   * Crea la descrizione del pagamento
   */
  public createPaymentDescription(adults: number, children: number, paymentType: 'deposit' | 'full'): string {
    const baseDescription = `C'è Aria di Fest - ${adults} adulti${children > 0 ? `, ${children} bambini` : ''}`;
    
    if (paymentType === 'full') {
      return `Pagamento completo ${baseDescription}`;
    } else {
      return `Acconto prenotazione ${baseDescription}`;
    }
  }

  /**
   * Valida i dettagli del pagamento
   */
  public validatePaymentDetails(paymentDetails: PaymentDetails): boolean {
    const requiredFields = ['amount', 'currency', 'status', 'timestamp', 'paymentType', 'paymentMethod'];
    
    for (const field of requiredFields) {
      if (!paymentDetails[field as keyof PaymentDetails]) {
        console.error(`Campo mancante nei dettagli del pagamento: ${field}`);
        return false;
      }
    }

    // Validazione specifica per provider
    if (paymentDetails.paymentMethod === 'paypal') {
      if (!paymentDetails.orderId || !paymentDetails.payerId) {
        console.error('Dettagli PayPal mancanti: orderId o payerId');
        return false;
      }
    } else if (paymentDetails.paymentMethod === 'stripe') {
      if (!paymentDetails.paymentIntentId) {
        console.error('Dettagli Stripe mancanti: paymentIntentId');
        return false;
      }
    }

    return true;
  }

  /**
   * Formatta i dettagli del pagamento per il salvataggio
   */
  public formatPaymentForStorage(paymentDetails: PaymentDetails): any {
    return {
      ...paymentDetails,
      provider: paymentDetails.paymentMethod,
      created_at: paymentDetails.timestamp,
      // Aggiungi altri campi necessari per il database
    };
  }

  /**
   * Ottiene la configurazione specifica per il provider
   */
  public getProviderConfig(provider: PaymentProvider): any {
    const config = configService.getConfig();
    
    switch (provider) {
      case 'paypal':
        return {
          clientId: config.paypal.clientId,
          currency: 'EUR',
          intent: 'capture'
        };
      case 'stripe':
        return {
          publicKey: config.stripe.publicKey,
          currency: 'eur'
        };
      default:
        throw new Error(`Provider non supportato: ${provider}`);
    }
  }

  /**
   * Verifica se un provider è disponibile
   */
  public isProviderAvailable(provider: PaymentProvider): boolean {
    const config = configService.getConfig();
    
    switch (provider) {
      case 'paypal':
        return !!config.paypal.clientId;
      case 'stripe':
        return !!config.stripe.publicKey;
      default:
        return false;
    }
  }

  /**
   * Ottiene la lista dei provider disponibili
   */
  public getAvailableProviders(): PaymentProvider[] {
    const providers: PaymentProvider[] = [];
    
    if (this.isProviderAvailable('paypal')) {
      providers.push('paypal');
    }
    
    if (this.isProviderAvailable('stripe')) {
      providers.push('stripe');
    }
    
    return providers;
  }

  /**
   * Log degli eventi di pagamento per debugging
   */
  public logPaymentEvent(event: string, details: any): void {
    if (configService.isDevelopment()) {
      console.log(`🔷 PaymentService - ${event}:`, details);
    }
  }

  /**
   * Crea un endpoint per il payment intent Stripe (da implementare nel backend)
   */
  public async createStripePaymentIntent(config: PaymentConfig): Promise<{ client_secret: string }> {
    const endpoint = `${configService.getBackendUrl()}/api/create-payment-intent`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: Math.round(config.amount * 100), // Stripe usa centesimi
        currency: config.currency,
        payment_type: config.paymentType,
        adults: config.adults,
        children: config.children,
        description: config.description
      }),
    });

    if (!response.ok) {
      throw new Error(`Errore nella creazione del Payment Intent: ${response.statusText}`);
    }

    return response.json();
  }
}

// Esporta istanza singleton
export const paymentService = PaymentService.getInstance();
export default paymentService;
